import { CheckCircle2 } from 'lucide-react'

export interface StepNavigatorProps {
  currentStep: number
  completedSteps: Set<number>
  totalSteps: number
  onStepClick: (step: number) => void
}

const STEP_LABELS: Record<number, string> = {
  1: 'Business Type',
  2: 'Verification',
  3: 'Industry',
  4: 'Account',
}

/**
 * StepNavigator displays the wizard progress and allows navigation
 * to completed steps. Uncompleted steps beyond the current frontier
 * are disabled.
 */
export function StepNavigator({
  currentStep,
  completedSteps,
  totalSteps,
  onStepClick,
}: StepNavigatorProps) {
  const isStepClickable = (step: number): boolean => {
    return step === currentStep || completedSteps.has(step)
  }

  return (
    <nav
      role="navigation"
      aria-label="Onboarding progress"
      className="w-full"
    >
      {/* Progress text */}
      <p className="text-sm text-secondary-500 mb-3 text-center">
        Step {currentStep} of {totalSteps}
      </p>

      {/* Step indicators */}
      <ol className="flex items-center w-full">
        {Array.from({ length: totalSteps }, (_, i) => i + 1).map((step) => {
          const isCompleted = completedSteps.has(step)
          const isCurrent = step === currentStep
          const isClickable = isStepClickable(step)

          return (
            <li
              key={step}
              className="flex-1 flex items-center"
            >
              {/* Step button */}
              <button
                type="button"
                onClick={() => isClickable && onStepClick(step)}
                disabled={!isClickable}
                aria-current={isCurrent ? 'step' : undefined}
                aria-disabled={!isClickable}
                aria-label={`${STEP_LABELS[step] || `Step ${step}`}${isCompleted ? ' (completed)' : isCurrent ? ' (current)' : ''}`}
                className={`
                  relative flex flex-col items-center gap-1 w-full group
                  ${isClickable ? 'cursor-pointer' : 'cursor-not-allowed'}
                `}
              >
                {/* Circle indicator */}
                <span
                  className={`
                    flex items-center justify-center w-8 h-8 rounded-full border-2 text-sm font-medium transition-colors
                    ${isCompleted
                      ? 'bg-success-500 border-success-500 text-white'
                      : isCurrent
                        ? 'bg-primary-600 border-primary-600 text-white'
                        : 'bg-white border-secondary-300 text-secondary-400'
                    }
                    ${isClickable && !isCurrent ? 'group-hover:border-primary-400 group-hover:text-primary-600' : ''}
                  `}
                >
                  {isCompleted ? (
                    <CheckCircle2 className="w-5 h-5" aria-hidden="true" />
                  ) : (
                    step
                  )}
                </span>

                {/* Step label */}
                <span
                  className={`
                    text-xs font-medium whitespace-nowrap
                    ${isCurrent
                      ? 'text-primary-700'
                      : isCompleted
                        ? 'text-success-700'
                        : 'text-secondary-400'
                    }
                  `}
                >
                  {STEP_LABELS[step] || `Step ${step}`}
                </span>
              </button>

              {/* Connector line between steps */}
              {step < totalSteps && (
                <div
                  className={`
                    flex-1 h-0.5 mx-1 mt-[-1rem]
                    ${completedSteps.has(step) ? 'bg-success-400' : 'bg-secondary-200'}
                  `}
                  aria-hidden="true"
                />
              )}
            </li>
          )
        })}
      </ol>
    </nav>
  )
}
